import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Empty, Field, Grid, Note, Panel, Spinner, Stat, TableWrap } from '@wyocspay/ui';
import { ApiError, api, type Banner, type BannerSize, type Merchant } from './api.js';
import { relative } from './format.js';

/**
 * Реклама на страницата за плащане, откъм платформата.
 *
 * Целият механизъм е наш: файлът се качва тук, съхранява се при нас, проверява
 * се по собствените си байтове и се сервира от нас. Няма рекламна мрежа, няма
 * външен скрипт, няма чужд домейн, от който браузърът да тегли каквото и да е.
 * Това не е предпочитание - Content-Security-Policy на страницата за плащане е
 * това, което държи платформата в обхвата на PCI DSS SAQ A, и една рекламна
 * интеграция не е причина да бъде разширена. Пълната аргументация е в
 * compliance/payment-page-advertising.md.
 *
 * Затова и този екран умишлено няма поле „код за вграждане“ или „URL на
 * изображение“. Приема се файл, и нищо друго.
 */

/**
 * Огледало на MAX_BANNER_BYTES в API, за екрана преди първото зареждане.
 *
 * Сървърът е този, който налага лимита, и той връща истинската стойност заедно
 * със списъка банери - оттам нататък се използва тя. Тази константа съществува
 * само за да има какво да пише на екрана, докато отговорът още не е дошъл.
 */
const MAX_BYTES = 400 * 1024;

/**
 * Какво предлага диалогът за избор на файл.
 *
 * Подсказка, не проверка: типът се определя на сървъра по водещите байтове на
 * самия файл. SVG липсва тук и се отказва там - SVG е документ, който може да
 * носи скрипт, а нищо изпълнимо няма работа на страница, на която някой въвежда
 * номер на карта.
 */
const ACCEPT = 'image/png,image/jpeg,image/webp';

function bytes(n: number): string {
  return n < 1024 ? `${n} B` : `${Math.round(n / 1024)} KB`;
}

/** Дял като процент. `null` означава, че няма от какво да се смята. */
function share(value: number | null): string {
  return value === null ? '-' : `${(value * 100).toFixed(0)}%`;
}

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error(`Файлът ${file.name} не може да се прочете`));
    reader.readAsDataURL(file);
  });
}

/** `YYYY-MM-DD` за <input type="date">, от ISO стойността на API. */
function dateValue(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

/**
 * Ден без час.
 *
 * Кампанията се продава за дни, не за минути, а часът в колоната само отнема
 * ширина от таблицата и кара окото да чете нещо, което няма значение.
 */
function day(iso: string): string {
  return new Date(iso).toLocaleDateString('bg-BG', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Дата от полето обратно към ISO.
 *
 * Краят на кампанията се тълкува като края на посочения ден, а не като неговото
 * начало: рекламодател, купил слота „до 31 август“, е купил и 31 август.
 */
function toIso(value: string, edge: 'start' | 'end'): string | null {
  if (!value) return null;
  return new Date(`${value}T${edge === 'start' ? '00:00:00' : '23:59:59'}Z`).toISOString();
}

// ---------------------------------------------------------------------------

/**
 * Преглед на самия банер.
 *
 * Байтовете искат Authorization заглавие, затова обикновен `src` към API-то би
 * получил 401. Изтеглят се със сесията и се показват през object URL, който
 * политиката на конзолата допуска - `img-src` тук включва `blob:`. Object URL-ът
 * се освобождава при подмяна и при напускане на екрана, за да не остане списък
 * от изображения жив в таба.
 */
function BannerImage({ id, height }: { id: string; height: number }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let created: string | null = null;
    let cancelled = false;

    void api
      .bannerImage(id)
      .then((blob) => {
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [id]);

  if (!url) {
    return (
      <div
        aria-hidden
        style={{ width: height * 2, height, border: '1px solid var(--wc-border)', background: 'var(--wc-surface)' }}
      />
    );
  }

  return (
    <img
      src={url}
      alt=""
      style={{
        display: 'block',
        height,
        maxWidth: 120,
        width: 'auto',
        objectFit: 'contain',
        border: '1px solid var(--wc-border)',
        background: 'var(--wc-surface)',
      }}
    />
  );
}

// ---------------------------------------------------------------------------

/**
 * Изискванията към файла, изписани ПРЕДИ да е избран файл.
 *
 * Досега екранът казваше само „PNG, JPEG или WebP до 400 KB“, а размерът в
 * пиксели не се проверяваше изобщо - тоест един банер можеше да е 3000 пиксела
 * широк, друг 200, и мястото на страницата за плащане си сменяше формата между
 * тях. Сега размерите са точно определени, а човек, който тръгва да иска
 * материал от рекламодател, трябва да види какво да поиска, преди да е стигнал
 * до диалога за избор на файл, а не да го научи от съобщение за грешка.
 *
 * Забележете какво този екран НЕ прави: не отваря избрания файл, за да му
 * измери размерите. Това би означавало браузърът тук да декодира изображение,
 * на което още не се вярва - а точно това е нещото, което сървърът отказва да
 * направи, защото файл, който обявява 60000x60000 пиксела, изяжда гигабайти в
 * мига, в който някой го декодира. Проверката се прави от заглавната част на
 * файла, на сървъра, и оттам идва и съобщението при отказ - то казва какъв е
 * файлът в действителност и кой размер се приема.
 */
function Requirements({ sizes, maxBytes }: { sizes: BannerSize[]; maxBytes: number }) {
  const slots = sizes.filter((s) => s.scale === 1);

  return (
    <div style={{ fontSize: 13, color: 'var(--wc-muted)', lineHeight: 1.6 }}>
      <div style={{ fontWeight: 600, color: 'var(--wc-body)' }}>Изисквания към файла</div>
      <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
        <li>
          Точни размери в пиксели - друг размер не се приема:
          <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
            {slots.map((slot) => {
              const retina = sizes.find((s) => s.slot === slot.slot && s.scale === 2);
              return (
                <li key={slot.slot}>
                  <strong className="wc-num">
                    {slot.width}x{slot.height}
                  </strong>{' '}
                  ({slot.label})
                  {retina ? (
                    <>
                      {' '}
                      или{' '}
                      <span className="wc-num">
                        {retina.width}x{retina.height}
                      </span>{' '}
                      за екрани с двойна плътност - показва се със същия размер като {slot.width}x
                      {slot.height}
                    </>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </li>
        <li>Най-много {bytes(maxBytes)}. Страница за плащане не чака реклама.</li>
        <li>
          PNG, JPEG или WebP. Типът се определя по водещите байтове на самия файл, а не по
          разширението и не по това, което браузърът е обявил.
        </li>
        <li>
          <strong>SVG не се приема.</strong> SVG е документ, а не картинка: може да носи скрипт и е
          единственият формат за изображения, който се изпълнява. Сервиран от нашия домейн, той би се
          изпълнил като нашия домейн - точно условието, което изважда страницата за плащане от PCI DSS
          SAQ A. Рекламодател, който има само SVG, го изнася като PNG.
        </li>
        <li>Без анимация - нито анимиран PNG, нито анимиран WebP, нито GIF.</li>
        <li>
          EXIF, XMP, цветови профили и коментари се премахват. Съхранява се файл, сглобен наново от
          частите, които носят самото изображение - не файлът, който е качен.
        </li>
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------

const EMPTY_FORM = {
  name: '',
  advertiser: '',
  destination_url: '',
  alt_text: '',
  merchant_id: '',
  starts_at: '',
  ends_at: '',
  weight: '1',
};

function NewBanner({
  merchants,
  sizes,
  maxBytes,
  onCreated,
}: {
  merchants: Merchant[];
  sizes: BannerSize[];
  maxBytes: number;
  onCreated: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set(key: keyof typeof EMPTY_FORM, value: string): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit(): Promise<void> {
    if (!file) {
      setError('Изберете файл с изображението.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.createBanner({
        name: form.name,
        advertiser: form.advertiser,
        destination_url: form.destination_url,
        alt_text: form.alt_text,
        merchant_id: form.merchant_id || null,
        starts_at: toIso(form.starts_at, 'start'),
        ends_at: toIso(form.ends_at, 'end'),
        weight: Number(form.weight) || 1,
        image: await toBase64(file),
      });
      setForm({ ...EMPTY_FORM });
      setFile(null);
      setOpen(false);
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <Panel>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 420px', minWidth: 0 }}>
            <Requirements sizes={sizes} maxBytes={maxBytes} />
          </div>
          <button type="button" className="wc-btn wc-btn-sm" onClick={() => setOpen(true)}>
            Нов банер
          </button>
        </div>
      </Panel>
    );
  }

  return (
    <Panel
      title="Нов банер"
      actions={
        <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => setOpen(false)}>
          Отказ
        </button>
      }
    >
      <Grid cols={2}>
        <Field label="Име" hint="Вътрешно - за да различавате два банера в конзолата.">
          <input className="wc-input" value={form.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Рекламодател" hint="Кой е купил мястото. Остава за фактуриране и за по-късни въпроси.">
          <input className="wc-input" value={form.advertiser} onChange={(e) => set('advertiser', e.target.value)} />
        </Field>
      </Grid>

      <Field
        label="Дестинация"
        hint="Пълен https адрес. Отваря се в нов таб с rel=noopener noreferrer nofollow, за да не може реклама да отведе клиент от започнато плащане."
      >
        <input
          className="wc-input"
          placeholder="https://"
          value={form.destination_url}
          onChange={(e) => set('destination_url', e.target.value)}
        />
      </Field>

      <Field
        label="Алтернативен текст"
        hint="Задължителен. Човек, който чете страницата с екранен четец, трябва да разбере какво е това изображение - на страница, на която ще похарчи пари."
      >
        <input className="wc-input" value={form.alt_text} onChange={(e) => set('alt_text', e.target.value)} />
      </Field>

      <Field label="Изображение">
        {/*
          Изискванията стоят над бутона, а не под него: човек, който избира
          файл, трябва да ги е прочел преди да го е избрал, иначе ги научава от
          съобщение за грешка.
        */}
        <div style={{ marginBottom: 10 }}>
          <Requirements sizes={sizes} maxBytes={maxBytes} />
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            ref={input}
            type="file"
            accept={ACCEPT}
            style={{ display: 'none' }}
            onChange={(e) => {
              const chosen = e.target.files?.[0] ?? null;
              e.target.value = '';
              if (chosen && chosen.size > maxBytes) {
                setError(`${chosen.name} е ${bytes(chosen.size)} - над лимита от ${bytes(maxBytes)}.`);
                return;
              }
              setError(null);
              setFile(chosen);
            }}
          />
          <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" onClick={() => input.current?.click()}>
            Избери файл
          </button>
          <span style={{ fontSize: 13, color: file ? 'var(--wc-body)' : 'var(--wc-faint)' }}>
            {file ? `${file.name} · ${bytes(file.size)}` : 'Няма избран файл'}
          </span>
        </div>
      </Field>

      <Grid cols={2}>
        <Field
          label="Насочване"
          hint="Празно означава страниците на всички търговци. Кампания, купена срещу конкретен търговец, изпреварва общата."
        >
          <select className="wc-input" value={form.merchant_id} onChange={(e) => set('merchant_id', e.target.value)}>
            <option value="">Всички търговци</option>
            {merchants.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Тежест" hint="Дял от показванията, когато няколко банера отговарят едновременно. От 1 до 100.">
          <input
            className="wc-input wc-num"
            value={form.weight}
            inputMode="numeric"
            onChange={(e) => set('weight', e.target.value)}
          />
        </Field>
      </Grid>

      <Grid cols={2}>
        <Field label="Начало" hint="По желание. Празно означава веднага след включване.">
          <input type="date" className="wc-input" value={form.starts_at} onChange={(e) => set('starts_at', e.target.value)} />
        </Field>
        <Field label="Край" hint="По желание. Включително посочения ден - кампанията спира сама в полунощ UTC.">
          <input type="date" className="wc-input" value={form.ends_at} onChange={(e) => set('ends_at', e.target.value)} />
        </Field>
      </Grid>

      {error ? <Note tone="danger">{error}</Note> : null}

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="button" className="wc-btn wc-btn-sm" disabled={busy} onClick={() => void submit()}>
          {busy ? 'Качване…' : 'Качи банера'}
        </button>
        <span style={{ fontSize: 12.5, color: 'var(--wc-faint)', alignSelf: 'center' }}>
          Новият банер се създава изключен. Нищо не се появява на страница за плащане, докато някой не
          го включи нарочно.
        </span>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------

function EditBanner({
  banner,
  onSaved,
  onCancel,
}: {
  banner: Banner;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [destination, setDestination] = useState(banner.destination_url);
  const [altText, setAltText] = useState(banner.alt_text);
  const [startsAt, setStartsAt] = useState(dateValue(banner.starts_at));
  const [endsAt, setEndsAt] = useState(dateValue(banner.ends_at));
  const [weight, setWeight] = useState(String(banner.weight));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.updateBanner(banner.id, {
        destination_url: destination,
        alt_text: altText,
        starts_at: toIso(startsAt, 'start'),
        ends_at: toIso(endsAt, 'end'),
        weight: Number(weight) || 1,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <td colSpan={9} style={{ background: 'var(--wc-blue-tint)' }}>
      <div style={{ padding: '12px 4px' }}>
        <Field label="Дестинация" hint="Само https. Адрес с потребител и парола не се приема - изглежда като друг домейн.">
          <input className="wc-input" value={destination} onChange={(e) => setDestination(e.target.value)} />
        </Field>
        <Field label="Алтернативен текст">
          <input className="wc-input" value={altText} onChange={(e) => setAltText(e.target.value)} />
        </Field>
        <Grid cols={3}>
          <Field label="Начало">
            <input type="date" className="wc-input" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
          </Field>
          <Field label="Край">
            <input type="date" className="wc-input" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
          </Field>
          <Field label="Тежест">
            <input
              className="wc-input wc-num"
              value={weight}
              inputMode="numeric"
              onChange={(e) => setWeight(e.target.value)}
            />
          </Field>
        </Grid>

        {error ? <Note tone="danger">{error}</Note> : null}

        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button type="button" className="wc-btn wc-btn-sm" disabled={busy} onClick={() => void save()}>
            Запази
          </button>
          <button type="button" className="wc-btn wc-btn-sm wc-btn-secondary" disabled={busy} onClick={onCancel}>
            Отказ
          </button>
        </div>
      </div>
    </td>
  );
}

// ---------------------------------------------------------------------------

export function AdsView() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [sizes, setSizes] = useState<BannerSize[]>([]);
  const [maxBytes, setMaxBytes] = useState(MAX_BYTES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ads, list] = await Promise.all([api.banners(), api.merchants()]);
      setBanners(ads.data);
      // Правилата идват от сървъра, който ги налага. Този екран ги изписва.
      setSizes(ads.accepted_sizes);
      setMaxBytes(ads.max_bytes);
      setMerchants(list.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const merchantName = useCallback(
    (id: string | null) => merchants.find((m) => m.id === id)?.name ?? id ?? '',
    [merchants],
  );

  /** Името на формата, както го връща сървърът, а не преведено тук. */
  const slotLabel = useCallback(
    (banner: Banner) => sizes.find((s) => s.slot === banner.slot)?.label ?? banner.slot ?? null,
    [sizes],
  );

  async function toggle(banner: Banner): Promise<void> {
    setBusy(banner.id);
    setMessage(null);
    try {
      await api.setBannerActive(banner.id, !banner.active);
      setMessage({
        tone: 'ok',
        text: banner.active
          ? `„${banner.name}“ е свален от страниците за плащане.`
          : `„${banner.name}“ вече се показва на страниците за плащане.`,
      });
      await load();
    } catch (err) {
      setMessage({ tone: 'danger', text: err instanceof ApiError ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  }

  const live = banners.filter((b) => b.active).length;
  const impressions = banners.reduce((n, b) => n + b.impressions, 0);
  const clicks = banners.reduce((n, b) => n + b.clicks, 0);

  return (
    /*
      `minmax(0, 1fr)` rather than the default `1fr`.
      A grid item is `min-width: auto`, which means it refuses to become
      narrower than its content - so the banner table below pushed this column
      wider than the console and the whole page scrolled sideways, taking the
      heading and the stat cards with it. Constraining the column lets the table
      scroll inside its own box, which is what TableWrap is for.
    */
    <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <Note title="Рекламата тук е наша - файл при нас, сервиран от нас">
        Банерът е изображение, което държим в собствената си база, и връзка, която се отваря в нов
        таб. Няма рекламна мрежа, няма чужд скрипт, няма външен домейн за изображения и няма пиксел
        за проследяване. Точно това позволява на страницата за плащане да остане в обхвата на PCI DSS
        SAQ A - а тя е в SAQ A, защото формата за плащане идва изцяло от Stripe и на страницата не се
        изпълнява нищо чуждо. Ако рекламодател поиска свой таг, отговорът е не: цената на
        алтернативата е PCI позицията на цялата платформа.
      </Note>

      {message ? <Note tone={message.tone}>{message.text}</Note> : null}

      {loading ? (
        <Spinner label="Зареждане…" />
      ) : error ? (
        <Note tone="danger">{error}</Note>
      ) : (
        <>
          <Grid cols={4}>
            <Stat label="Банери" value={banners.length} hint={`${live} включени`} />
            <Stat label="Показвания" value={impressions.toLocaleString('bg-BG')} accent="var(--wc-green)" />
            <Stat label="Кликове" value={clicks.toLocaleString('bg-BG')} accent="var(--wc-blue)" />
            <Stat
              label="Кликнати"
              value={impressions > 0 ? `${((clicks / impressions) * 100).toFixed(2)}%` : '-'}
              hint="Отчетено от нашата страница, не от независимо измерване"
              accent="var(--wc-grey)"
            />
          </Grid>

          <NewBanner
            merchants={merchants}
            sizes={sizes}
            maxBytes={maxBytes}
            onCreated={() => void load()}
          />

          <Panel title="Банери" padded={false}>
            {banners.length === 0 ? (
              <Empty>Още няма качени банери.</Empty>
            ) : (
              <TableWrap>
                <table className="wc-table">
                  <thead>
                    <tr>
                      <th>Банер</th>
                      <th>Формат</th>
                      <th>Дестинация</th>
                      <th>Насочване</th>
                      <th>Период</th>
                      <th className="wc-right">Дял</th>
                      <th className="wc-right">Показвания</th>
                      <th className="wc-right">Кликове</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {banners.map((b) =>
                      editing === b.id ? (
                        <tr key={b.id}>
                          <EditBanner
                            banner={b}
                            onCancel={() => setEditing(null)}
                            onSaved={() => {
                              setEditing(null);
                              void load();
                            }}
                          />
                        </tr>
                      ) : (
                        <tr key={b.id}>
                          <td>
                            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                              <BannerImage id={b.id} height={38} />
                              <div style={{ minWidth: 0, maxWidth: 160 }}>
                                <strong>{b.name}</strong>{' '}
                                <Badge tone={b.active ? 'ok' : 'neutral'}>
                                  {b.active ? 'НА СТРАНИЦАТА' : 'ИЗКЛЮЧЕН'}
                                </Badge>
                                <div style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>{b.advertiser}</div>
                                {/*
                                  Алтернативният текст стои до банера, а не зад
                                  иконка: това е текстът, който чува човек с
                                  екранен четец, и единственият начин да се
                                  забележи, че е безполезен, е да се вижда.
                                */}
                                <div
                                  title={b.alt_text}
                                  style={{
                                    fontSize: 12,
                                    color: 'var(--wc-faint)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  alt: {b.alt_text}
                                </div>
                                <div style={{ fontSize: 11.5, color: 'var(--wc-faint)' }}>
                                  {b.mime_type} · {bytes(b.size)} · качен {relative(b.created_at)}
                                </div>
                              </div>
                            </div>
                          </td>
                          {/*
                            Форматът, както е бил определен от заглавната част
                            на самия файл при качването.

                            Банер без формат е качен, преди размерите да бъдат
                            въведени - тогава се приемаше всякакъв размер. Стои
                            като предупреждение, а не като тире: такъв материал
                            не пасва на запазеното място на страницата и трябва
                            да се качи наново в приет размер.
                          */}
                          <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                            {b.slot && b.width && b.height ? (
                              <>
                                <span className="wc-num">
                                  {b.width}x{b.height}
                                </span>
                                <div style={{ color: 'var(--wc-muted)' }}>
                                  {slotLabel(b)}
                                  {sizes.some((s) => s.slot === b.slot && s.scale === 2 && s.width === b.width)
                                    ? ' · 2x'
                                    : ''}
                                </div>
                              </>
                            ) : (
                              <Badge tone="warn">НЕИЗВЕСТЕН РАЗМЕР</Badge>
                            )}
                          </td>
                          <td style={{ fontSize: 12.5, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {/*
                              Показва се, но не е връзка. Конзолата няма причина
                              да отваря дестинация на рекламодател с едно
                              случайно кликване; адресът се чете, и толкова.
                            */}
                            <span title={b.destination_url}>{b.destination_url}</span>
                          </td>
                          <td style={{ fontSize: 13 }}>
                            {b.merchant_id ? (
                              <Badge tone="info" icon={false}>{merchantName(b.merchant_id)}</Badge>
                            ) : (
                              <span style={{ color: 'var(--wc-muted)' }}>всички търговци</span>
                            )}
                          </td>
                          <td style={{ fontSize: 12.5, color: 'var(--wc-muted)', whiteSpace: 'nowrap' }}>
                            {b.starts_at || b.ends_at ? (
                              <>
                                {b.starts_at ? day(b.starts_at) : 'веднага'}
                                <div>{b.ends_at ? `до ${day(b.ends_at)}` : 'без край'}</div>
                              </>
                            ) : (
                              'без период'
                            )}
                          </td>
                          {/*
                            Какъв дял получава банерът в действителност, и какъв
                            иска тежестта му.

                            Двете числа стоят едно до друго, защото поотделно не
                            отговарят на нищо. „Тежест 3“ не казва дали ротацията
                            работи; „34% срещу очаквани 33%“ казва. Измереното е
                            за последната седмица - за целия живот на банера
                            числото щеше да включва всяка седмица, в която са
                            вървели други банери, и отдавна включен материал щеше
                            да изглежда ощетен само защото е нов.

                            Дял се показва само за банер, който в момента влиза в
                            ротацията. За изключен или извън периода си банер
                            няма какво да се сравнява и се показва тежестта.
                          */}
                          <td className="wc-right" style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                            {b.eligible ? (
                              <>
                                <span className="wc-num" style={{ fontSize: 13.5 }}>
                                  {share(b.share)}
                                </span>
                                <div style={{ color: 'var(--wc-muted)' }}>
                                  очаквани {share(b.expected_share)}
                                </div>
                                <div style={{ color: 'var(--wc-faint)', fontSize: 11.5 }}>
                                  тежест {b.weight}
                                </div>
                              </>
                            ) : (
                              <span style={{ color: 'var(--wc-faint)' }}>тежест {b.weight}</span>
                            )}
                          </td>
                          <td className="wc-right wc-num">
                            {b.impressions.toLocaleString('bg-BG')}
                            <div style={{ fontSize: 11.5, color: 'var(--wc-faint)' }}>
                              {b.recent_impressions.toLocaleString('bg-BG')} за 7 дни
                            </div>
                          </td>
                          <td className="wc-right wc-num">{b.clicks.toLocaleString('bg-BG')}</td>
                          {/*
                            Двата бутона един под друг, а не един до друг.
                            Редът и без това е висок колкото описанието вляво, а
                            изправената колона спестява около седемдесет пиксела
                            - точно те решават дали действията се виждат на
                            екран с ширина 1280 или трябва да се търсят с
                            хоризонтално скролване.
                          */}
                          <td className="wc-right" style={{ whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 6, alignItems: 'stretch' }}>
                              <button
                                type="button"
                                className={`wc-btn wc-btn-sm ${b.active ? 'wc-btn-danger' : ''}`}
                                disabled={busy === b.id}
                                onClick={() => void toggle(b)}
                              >
                                {b.active ? 'Спри' : 'Включи'}
                              </button>
                              <button
                                type="button"
                                className="wc-btn wc-btn-sm wc-btn-secondary"
                                onClick={() => setEditing(b.id)}
                              >
                                Редакция
                              </button>
                            </div>
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </TableWrap>
            )}
          </Panel>

          <Note title="Един банер на страница, и как се редува">
            На страница за плащане се показва точно една реклама или нито една - никога две. Кой е
            следващият не се тегли на случаен принцип: банерите се подреждат предварително в
            последователност, в която всеки се среща толкова пъти, колкото е тежестта му, разпределени
            възможно най-равномерно, и сървърът върви по нея с една стъпка на всяко показване. Затова
            един и същ банер не се пада два пъти подред, освен ако тежестта му не надхвърля половината
            от общата - тогава е аритметично неизбежно и се вижда в колоната „Дял“. Броячът е при нас,
            на сървъра, и знае само докъде е стигнала ротацията при този търговец: няма бисквитка и
            няма нищо, което да се върже към конкретен платец.
          </Note>

          <Note title="За какво отговарят числата">
            Показванията се броят, когато изображението наистина се е показало, а не когато е било
            поискано. Броенето изисква жив линк за плащане, което пречи на брояча да бъде надуван
            отвън, но не го прави независимо измерване - това е нашата собствена страница, която
            отчита какво е нарисувала, и точно така се представя пред рекламодателите. Срещу банер не
            се пази нищо друго: няма бисквитка, няма идентификатор на платеца, няма ред на всяко
            показване - само едно число на банер за всеки ден.
            <br />
            <br />
            Дялът се смята за последните седем дни и в рамките на собствената ротация на банера:
            насочен банер се сравнява с останалите насочени към същия търговец, общ - с останалите
            общи. Общ банер може да е под очаквания си дял, без нещо да е счупено: търговец, който има
            своя кампания, взима цялото място на своите страници и общите банери просто не са били в
            играта там.
          </Note>
        </>
      )}
    </div>
  );
}
